import { useState, useEffect } from "react";
import { db, storage } from "../firebase/config";
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../Context/AuthContext";
import { v4 as uuidv4 } from "uuid";
import "../styles/EmployeeSubmit.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import { getSlaPolicy, ensureTicketSla } from "../utils/sla";
import { DateTime } from "luxon";

const sendCompanyNotification = async (companyId, docId, payload) => {
  const nref = doc(db, `companies/${companyId}/notifications`, docId);
  await setDoc(
    nref,
    { archived: false, read: false, timestamp: serverTimestamp(), ...payload },
    { merge: true }
  );
};

// ---------- Priority derived from Impact + Urgency ----------
const PRIORITY_MATRIX = {
  low: {
    low: "Low",
    medium: "Low",
    high: "Medium",
    critical: "Medium",
  },
  medium: {
    low: "Low",
    medium: "Medium",
    high: "High",
    critical: "High",
  },
  high: {
    low: "Medium",
    medium: "High",
    high: "High",
    critical: "Critical",
  },
  critical: {
    low: "Medium",
    medium: "High",
    high: "Critical",
    critical: "Critical",
  },
};

function derivePriority(impact = "Low", urgency = "Low") {
  const i = (impact || "Low").toLowerCase();
  const u = (urgency || "Low").toLowerCase();
  return PRIORITY_MATRIX[i]?.[u] || "Low";
}
// ------------------------------------------------------------

export default function EmployeeSubmit() {
  const { user } = useAuth();
  const companyId = user?.companyId;

  const [activeTab, setActiveTab] = useState("submit");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [impact, setImpact] = useState("");
  const [urgency, setUrgency] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotName, setScreenshotName] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submittedTickets, setSubmittedTickets] = useState([]);
  const [filterStatus, setFilterStatus] = useState("All");
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");

  useEffect(() => {
    const fetchTickets = async () => {
      if (!companyId || !user?.uid) return;
      try {
        setLoading(true);
        const q = query(
          collection(db, `companies/${companyId}/tickets`),
          where("submittedBy", "==", user.uid)
        );
        const snapshot = await getDocs(q);
        setSubmittedTickets(
          snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
      } catch (err) {
        console.error("Failed to fetch tickets:", err.message);
        setError("Failed to fetch tickets.");
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [companyId, user]);

  useEffect(() => {
    const fetchTimeZone = async () => {
      if (!companyId) return;
      try {
        const companyDoc = await getDoc(doc(db, "companies", companyId));
        const tz = companyDoc.data()?.timeZone || "UTC";
        setCompanyTimeZone(tz);
      } catch (err) {
        console.error("Failed to fetch time zone:", err.message);
      }
    };
    fetchTimeZone();
  }, [companyId]);

  const generateIncidentNo = () => {
    const date = new Date();
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `INC-${y}${m}${d}-${Math.floor(1000 + Math.random() * 9000)}`;
  };

  const handleFileUpload = async () => {
    if (!screenshot) return null;
    const fileRef = ref(storage, `screenshots/${uuidv4()}-${screenshot.name}`);
    await uploadBytes(fileRef, screenshot);
    return await getDownloadURL(fileRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !companyId) {
      setError("Company ID missing. Cannot submit ticket.");
      return;
    }
    try {
      setLoading(true);
      const screenshotURL = await handleFileUpload();

      const createdAt = new Date();
      const displayName =
        user?.displayName ||
        user?.name ||
        user?.username ||
        user?.email?.split("@")[0];

      // ✅ auto-calc priority from impact + urgency
      const derivedPriority = derivePriority(impact, urgency);

      const baseTicket = {
        incidentNo: generateIncidentNo(),
        title,
        description,
        priority: derivedPriority,
        category,
        impact,
        urgency,
        screenshotURL: screenshotURL || null,
        status: "Open",
        submittedBy: user.uid,
        submittedByName: displayName,
        createdAt,
        helpdeskComment: "",
      };

      const docRef = await addDoc(
        collection(db, `companies/${companyId}/tickets`),
        baseTicket
      );

      try {
        const policy = await getSlaPolicy(companyId);
        await ensureTicketSla(companyId, docRef.id, baseTicket, policy);
      } catch (e) {
        console.warn("SLA init skipped:", e);
      }

      await sendCompanyNotification(companyId, `ticketSubmitted_${docRef.id}`, {
        type: "ticketSubmitted",
        ticketId: docRef.id,
        employeeId: user.uid,
        employeeName: displayName,
        priority: derivedPriority,
        category,
        title,
        severity: (derivedPriority || "Low").toLowerCase(),
        message: `${displayName} submitted a ${derivedPriority} ${category} ticket: “${title}”.`,
      });

      // reset form
      setTitle("");
      setDescription("");
      setCategory("");
      setImpact("");
      setUrgency("");
      setScreenshot(null);
      setScreenshotName("");

      toast.success("Ticket submitted successfully!");
    } catch (err) {
      console.error("Submission error:", err.message);
      setError("Failed to submit ticket.");
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets =
    filterStatus === "All"
      ? submittedTickets
      : submittedTickets.filter((t) => t.status === filterStatus);

  const formatWhen = (tsLike) => {
    const d = tsLike?.toDate ? tsLike.toDate() : tsLike;
    if (!d) return "—";
    return DateTime.fromJSDate(d, { zone: companyTimeZone }).toFormat(
      "ccc, dd LLL yyyy • hh:mm a"
    );
  };

  return (
    <div className="employeesubmit-outer">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <nav className="employeesubmit-breadcrumbbar">
        <p className="employeesubmit-breadcrumb">
          <Link to="/employee-dashboard">Dashboard</Link> / <span>Tickets</span>
        </p>
      </nav>

      <header className="employeesubmit-header">
        <div>
          <h1>Help Desk</h1>
          <p className="muted">
            Submit a ticket or review your previous requests.
          </p>
        </div>
      </header>

      <div className="employeesubmit-tabs">
        <button
          className={`employeesubmit-tab ${
            activeTab === "submit" ? "active" : ""
          }`}
          onClick={() => setActiveTab("submit")}
        >
          New Ticket
        </button>
        <button
          className={`employeesubmit-tab ${
            activeTab === "view" ? "active" : ""
          }`}
          onClick={() => setActiveTab("view")}
        >
          Ticket History
        </button>
      </div>

      {activeTab === "submit" && (
        <section className="employeesubmit-card" aria-live="polite">
          {error && <div className="es-alert es-alert--error">{error}</div>}

          <form onSubmit={handleSubmit} className="employeesubmit-form">
            <div className="es-grid">
              <div className="es-field">
                <label htmlFor="title">Title</label>
                <input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief, clear summary"
                  required
                />
              </div>

              <div className="es-field">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  <option value="">Select</option>
                  <option>Inquiry/Help</option>
                  <option>Hardware</option>
                  <option>Software</option>
                  <option>Network</option>
                  <option>Database</option>
                </select>
              </div>

              {/* Priority removed from employee form */}

              <div className="es-field">
                <label htmlFor="impact">Impact</label>
                <select
                  id="impact"
                  value={impact}
                  onChange={(e) => setImpact(e.target.value)}
                  required
                >
                  <option value="">Select</option>
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>

              <div className="es-field">
                <label htmlFor="urgency">Urgency</label>
                <select
                  id="urgency"
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value)}
                  required
                >
                  <option value="">Select</option>
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>

              <div className="es-field">
                <label>Attach Screenshot (optional)</label>
                <div className="es-filewrap">
                  <input
                    id="file"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      setScreenshot(f || null);
                      setScreenshotName(f ? f.name : "");
                    }}
                  />
                  <label htmlFor="file" className="es-filebtn">
                    Choose file
                  </label>
                  <span className="es-filename">
                    {screenshotName || "No file chosen"}
                  </span>
                </div>
              </div>

              <div className="es-field es-col-span-2">
                <label htmlFor="desc">Description</label>
                <textarea
                  id="desc"
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Include steps to reproduce, expected vs. actual behavior, and any error messages."
                  required
                />
              </div>
            </div>

            <div className="es-actions">
              <button
                type="submit"
                className="employeesubmit-submit"
                disabled={loading}
              >
                {loading ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </section>
      )}

      {activeTab === "view" && (
        <section className="employeesubmit-card">
          <div className="employeesubmit-history-controls">
            <label htmlFor="statusFilter">Filter by Status:</label>
            <select
              id="statusFilter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="All">All</option>
              <option>Open</option>
              <option>In Progress</option>
              <option>Closed</option>
            </select>
          </div>

          {filteredTickets.length === 0 ? (
            <div className="es-empty">
              <h3>No tickets found</h3>
              <p className="muted">
                Your tickets will appear here. Try changing the filter above.
              </p>
            </div>
          ) : (
            <div className="employeesubmit-table-wrapper">
              <table className="employeesubmit-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Impact</th>
                    <th>Urgency</th>
                    <th>Priority</th>
                    <th>Comment</th>
                    <th>File</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map((t) => (
                    <tr key={t.id}>
                      <td>{formatWhen(t.createdAt)}</td>
                      <td>{t.incidentNo}</td>
                      <td className="es-titlecell">{t.title}</td>
                      <td>{t.category}</td>
                      <td>
                        <span
                          className={`es-badge ${(t.status || "")
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {t.status}
                        </span>
                      </td>
                      <td>{t.impact}</td>
                      <td>{t.urgency}</td>
                      <td>{t.priority}</td>
                      <td>{t.helpdeskComment || "—"}</td>
                      <td>
                        {t.screenshotURL ? (
                          <a
                            href={t.screenshotURL}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
